import { Plus } from "lucide-react";
import { useCallback, useMemo } from "react";
import { AlertIcon } from "../../../components/icons/AlertIcon";
import { CalendarIcon } from "../../../components/icons/CalendarIcon";
import { ErrorPlaceholder, PageHeader } from "../../../components/common";
import { Button } from "../../../components/button";
import { useToast } from "../../../components/toast-container";
import { NotificationDataTable } from "../components/data-table";
import { NotificationModals } from "../components/NotificationModals";
import { NotificationToolbar } from "../components/NotificationToolbar";
import {
  NotificationStatsSkeleton,
  NotificationTableSkeleton,
} from "../components/notification-skeletons";
import { useDeleteNotification } from "../hooks/useDeleteNotification";
import { useGetNotificationList } from "../hooks/useGetNotificationList";
import { useUpdateNotificationPin } from "../hooks/useUpdateNotificationPin";
import { useTranslation } from "react-i18next";
import { useState } from "react";
import { useGetStatisticsNotification } from "../hooks/useGetStatisticsNotification";
import { useNavigate } from "react-router-dom";
import { formatDateTime } from "../../../utils/dateTime";
import { isNotificationOlderThan24Hours } from "../utils/notificationDates";
import type { PublicOption } from "../../common/public-options/publicOptions.api";
import { useGetAlertTypeOptions } from "../../common/public-options";

function NotificationPage() {
  const { t } = useTranslation();
  const { addToast } = useToast();
  const {
    notifications,
    loading,
    error: listError,
    filters,
    setFilters,
    updateFilter,
    sorting,
    updateSorting,
    page,
    setPage,
    totalCount,
    itemsPerPage,
    refreshNotifications,
    removeNotification,
    setNotificationPinned,
  } = useGetNotificationList();
  const {
    deleteNotification,
    loading: deleting,
    error: deleteError,
  } = useDeleteNotification();
  const {
    updateNotificationPin,
    updatingIds: pinningIds,
    error: pinError,
  } = useUpdateNotificationPin();

  const {
    statistics: notificationStats,
    loading: statsLoading,
    error: statsError,
    refresh: refreshStats,
  } = useGetStatisticsNotification();
  const { alertTypes } = useGetAlertTypeOptions();
  const alertTypeOptions = useMemo<PublicOption[]>(
    () =>
      alertTypes.map((alertType) => ({
        label: alertType.label,
        value: alertType.value,
      })),
    [alertTypes],
  );

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [notificationToDelete, setNotificationToDelete] = useState<{
    id: string;
    title: string;
  } | null>(null);
  const [pinDialogOpen, setPinDialogOpen] = useState(false);
  const [notificationToPin, setNotificationToPin] = useState<{
    id: string;
    is_pinned?: boolean;
  } | null>(null);
  const [dontShowAgain, setDontShowAgain] = useState(false);

  // Check localStorage for "don't show again" setting
  const getDontShowAgainSetting = () => {
    try {
      const saved = localStorage.getItem("notification-pin-dialog-dont-show");
      return saved === "true";
    } catch (error) {
      console.error("Error reading localStorage:", error);
      return false;
    }
  };

  // Save "don't show again" setting to localStorage
  const saveDontShowAgainSetting = (value: boolean) => {
    try {
      localStorage.setItem("notification-pin-dialog-dont-show", value.toString());
    } catch (error) {
      console.error("Error saving to localStorage:", error);
    }
  };

  // Use real statistics from API
  const stats = [
    { label: t("notification.total"), value: notificationStats?.total || 0 },
    { label: t("notification.this_week"), value: notificationStats?.this_week || 0 },
  ];

  const navigate = useNavigate();

  const refreshNotificationPage = useCallback(async () => {
    await Promise.all([refreshNotifications(), refreshStats()]);
  }, [refreshNotifications, refreshStats]);

  const statsWithIcons = [
    { ...stats[0], icon: <AlertIcon className="text-emerald-500" /> },
    { ...stats[1], icon: <CalendarIcon className="text-green-500" /> },
  ];

  const handleCreateNotification = () => {
    navigate("/notification/create");
  };

  const handleEditNotification = (notification: { id: string }) => {
    navigate(`/notification/${notification.id}`);
  };

  const handleDeleteClick = (notification: { id: string; title: string }) => {
    setNotificationToDelete(notification);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!notificationToDelete) return;

    try {
      await deleteNotification(notificationToDelete.id);
      removeNotification(notificationToDelete.id);
      // Refresh statistics after successful deletion
      await refreshStats();
      setDeleteDialogOpen(false);
      setNotificationToDelete(null);
    } catch {
      // The delete hook owns the failure message; keep the dialog open so the
      // user can retry from the inline error.
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setNotificationToDelete(null);
  };

  const handlePinToggle = async (notification: {
    id: string;
    is_pinned?: boolean;
    created_at: string;
  }) => {
    // If unpinning, no confirmation needed
    if (notification.is_pinned) {
      try {
        await updateNotificationPin(notification.id, false);
        setNotificationPinned(notification.id, false);
      } catch (unpinError: unknown) {
        addToast({
          type: "error",
          title: "Error!",
          message:
            unpinError instanceof Error
              ? unpinError.message
              : "Failed to update notification",
        });
      }
      return;
    }

    // If notification is older than 24 hours, explain why pinning is blocked
    if (isNotificationOlderThan24Hours(notification.created_at)) {
      addToast({
        type: "warning",
        title: t("notification.pin_notification"),
        message: t("notification.pin_disabled_tooltip"),
      });
      return;
    }

    // If pinning, check if user has disabled the confirmation dialog
    const dontShowSetting = getDontShowAgainSetting();
    if (dontShowSetting) {
      // User has disabled the dialog, pin directly
      try {
        await updateNotificationPin(notification.id, true);
        setNotificationPinned(notification.id, true);
      } catch (pinError: unknown) {
        addToast({
          type: "error",
          title: "Error!",
          message:
            pinError instanceof Error
              ? pinError.message
              : "Failed to update notification",
        });
      }
      return;
    }

    // Show confirmation dialog
    setNotificationToPin(notification);
    setPinDialogOpen(true);
  };

  const handlePinConfirm = async () => {
    if (!notificationToPin) return;

    try {
      await updateNotificationPin(notificationToPin.id, true);
      setNotificationPinned(notificationToPin.id, true);
      // Save "don't show again" setting if checked
      if (dontShowAgain) {
        saveDontShowAgainSetting(true);
      }

      setPinDialogOpen(false);
      setNotificationToPin(null);
      setDontShowAgain(false);
    } catch {
      // The pin hook owns the failure message; keep the dialog open so the
      // user can retry from the inline error.
    }
  };

  const handlePinCancel = () => {
    setPinDialogOpen(false);
    setNotificationToPin(null);
    setDontShowAgain(false);
  };

  const totalPages = Math.ceil(totalCount / itemsPerPage);

  const hasActiveListFilters = useMemo(
    () =>
      Boolean(filters.keyword?.trim()) ||
      Boolean(filters.alert_type_id) ||
      Boolean(filters.pin_status) ||
      Boolean(filters.notification_date) ||
      Boolean(filters.title) ||
      Boolean(filters.content),
    [filters],
  );

  const emptyLabel = hasActiveListFilters
    ? t("notification.no_notifications_matching")
    : t("notification.no_notifications_available");

  return (
    <>
      <div className="min-h-screen bg-background">
        <PageHeader
          title={t("notification.center")}
          subtitle={t("notification.subtitle")}
          actions={
            <Button
              className="flex items-center gap-2"
              onClick={handleCreateNotification}
            >
              <Plus className="h-4 w-4" />
              {t("notification.add_new")}
            </Button>
          }
          sticky
        />
        <div className="px-6 py-6">
          {statsLoading ? (
            <NotificationStatsSkeleton />
          ) : statsError ? (
            <div className="mb-4">
              <ErrorPlaceholder
                error={statsError}
                onRetry={() => void refreshStats()}
              />
            </div>
          ) : (
            <div className="mb-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
              {statsWithIcons.map((stat) => (
                <div
                  key={stat.label}
                  className="relative rounded-lg border border-gray-200 bg-white p-4 dark:border-[#394150] dark:bg-[#212936]"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="text-2xl font-bold text-foreground">{stat.value}</div>
                      <div className="mb-1 text-sm text-foreground">{stat.label}</div>
                    </div>
                    <div className="ml-4 flex h-8 w-8 items-center justify-center rounded-lg bg-[#F2FCFA] text-emerald-600 dark:bg-emerald-900/30 dark:text-emerald-400">
                      {stat.icon}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          <NotificationToolbar
            filters={filters}
            setFilters={setFilters}
            loading={loading}
            onRefresh={refreshNotificationPage}
          />

          <div className="text-xl font-semibold mb-5">{t("notification.notification_list")}</div>
          {loading ? (
            <NotificationTableSkeleton />
          ) : (
            <NotificationDataTable
              notifications={notifications}
              alertTypes={alertTypeOptions}
              filters={{
                title: filters.title ?? "",
                content: filters.content ?? "",
                alertTypeId: filters.alert_type_id ?? "",
              }}
              emptyLabel={emptyLabel}
              sortKey={sorting.key}
              sortDirection={sorting.direction}
              onFilterChange={(key, value) => {
                const requestKey = key === "alertTypeId" ? "alert_type_id" : key;
                updateFilter(requestKey, value);
              }}
              onSort={updateSorting}
              loading={false}
              error={listError}
              onRetry={refreshNotifications}
              page={page}
              totalPages={totalPages}
              onPageChange={setPage}
              onRowClick={handleEditNotification}
              updatingIds={pinningIds}
              formatDate={formatDateTime}
              isOlderThan24Hours={isNotificationOlderThan24Hours}
              onEdit={handleEditNotification}
              onPinToggle={handlePinToggle}
              onDelete={handleDeleteClick}
            />
          )}
        </div>
      </div>

      <NotificationModals
        deleteDialogOpen={deleteDialogOpen}
        notificationTitle={notificationToDelete?.title}
        deleting={deleting}
        deleteError={deleteError}
        onDeleteCancel={handleDeleteCancel}
        onDeleteConfirm={handleDeleteConfirm}
        pinDialogOpen={pinDialogOpen}
        isPinning={pinningIds.has(notificationToPin?.id || "")}
        pinError={pinError}
        dontShowAgain={dontShowAgain}
        onDontShowAgainChange={setDontShowAgain}
        onPinCancel={handlePinCancel}
        onPinConfirm={handlePinConfirm}
      />
    </>
  );
}

export default NotificationPage;
